import { type QueryClient } from "@tanstack/react-query";
import { type LoaderFunctionArgs } from "react-router";

import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";

import { sessionQueryKeys } from "../hooks/sessionQueryKeys";
import { SessionService } from "../SessionService";

/** How long the gate reuses a cached session record. One minute, as for datasets. */
const SESSION_STALE_TIME_MS = 60_000;

/**
 * The org that owns the `:sessionId` route's session, for a route gate. Fetches
 * the record under the key `useSession` reads, so the page reuses it.
 * `undefined` when the route names no session or the record cannot be fetched.
 */
export async function sessionOrgId(
  { params }: LoaderFunctionArgs,
  queryClient: QueryClient,
): Promise<string | undefined> {
  const sessionId = params.sessionId;
  if (sessionId === undefined) {
    return undefined;
  }
  try {
    const session = await queryClient.fetchQuery({
      queryKey: sessionQueryKeys.detail(sessionId),
      queryFn: async ({ signal }) =>
        new SessionService(await makeAuthenticatedHttpClient()).getSessionById(
          sessionId,
          { abortSignal: signal },
        ),
      // A session's org never changes, so a cached record answers.
      staleTime: SESSION_STALE_TIME_MS,
    });
    return session.org_id;
  } catch {
    return undefined;
  }
}
